import axios from '../../config/axios';
import { runSourceDocumentJob, SOURCE_DOCUMENT_JOB_TYPES } from '../../services/sourceDocumentJob.service';

// Base URL mirrors documentEditor.service.js so the IND routes live next to the
// Protocol/SAP/ICF ones: /api/documentEditor/ind (see ind-package-spec section 4).
// eslint-disable-next-line no-undef
const ENV_API_URL = process.env.REACT_APP_API_URL;
const API_URL = `${(
  ENV_API_URL ||
  'https://api-backend-clinical-intake-uat.dizzaroo.com'
).replace(/\/$/, '')}/api/documentEditor/ind`;

// Normalise axios failures into a plain Error carrying the server's message, so
// callers can show `err.message` without inspecting response shapes.
function toError(error) {
  if (error instanceof Error && !error.response) return error;
  const data = error?.response?.data;
  const message =
    (typeof data === 'string' && data) ||
    data?.error ||
    data?.message ||
    error?.message ||
    'Request failed';
  const wrapped = new Error(message);
  wrapped.response = error?.response;
  wrapped.status = error?.response?.status;
  return wrapped;
}

async function request(promise) {
  try {
    const response = await promise;
    return response.data;
  } catch (error) {
    throw toError(error);
  }
}

// ---------------------------------------------------------------------------
// Catalogue
// ---------------------------------------------------------------------------

/** GET /catalogue -> { documents: [...] } (prompts omitted). */
export async function getCatalogue() {
  return request(axios.get(`${API_URL}/catalogue`));
}

// ---------------------------------------------------------------------------
// Intake
// ---------------------------------------------------------------------------

/** GET /intake/:projectId -> { success, intake } (lazily created server-side). */
export async function getIntake(projectId) {
  return request(axios.get(`${API_URL}/intake/${projectId}`));
}

/** PUT /intake/:projectId with a partial intake (deep-merged per block; arrays replaced). */
export async function updateIntake(projectId, patch) {
  return request(axios.put(`${API_URL}/intake/${projectId}`, patch || {}));
}

/** POST /intake/:projectId/auto-populate -> { success, intake, populatedFields }. */
export async function autoPopulateIntake(projectId) {
  return request(axios.post(`${API_URL}/intake/${projectId}/auto-populate`, {}, { timeout: 5 * 60 * 1000 }));
}

/**
 * POST /intake/:projectId/extract-nonclinical (multipart files[]) -> { success, intake, studiesAdded }.
 * @param {string} projectId
 * @param {File[]|FileList} files pdf/docx nonclinical reports
 */
export async function extractNonclinical(projectId, files) {
  const form = new FormData();
  Array.from(files || []).forEach((file) => form.append('files', file, file.name));
  return request(
    axios.post(`${API_URL}/intake/${projectId}/extract-nonclinical`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 10 * 60 * 1000,
    })
  );
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

/** GET /all?projectId&documentKey -> [sections] sorted by documentKey, order. */
export async function getAllSections(projectId, documentKey) {
  const params = { projectId };
  if (documentKey) params.documentKey = documentKey;
  const data = await request(axios.get(`${API_URL}/all`, { params }));
  return Array.isArray(data) ? data : data?.sections || [];
}

/** POST /initialize { projectId, documentKey } -> { created, sections }. */
export async function initializeDocument(projectId, documentKey) {
  return request(axios.post(`${API_URL}/initialize`, { projectId, documentKey }));
}

/** GET /:id?projectId -> section. */
export async function getSection(sectionId, projectId) {
  const data = await request(axios.get(`${API_URL}/${sectionId}`, { params: { projectId } }));
  return data?.document || data?.section || data;
}

/** PUT /update/:id { projectId, output?, context?, prompt?, final?, provenance? } -> section. */
export async function updateSection(sectionId, updateData, projectId) {
  const data = await request(axios.put(`${API_URL}/update/${sectionId}`, { ...updateData, projectId }));
  return data?.document || data?.section || data;
}

/** DELETE /delete/:id { projectId } -> { message }. */
export async function deleteSection(sectionId, projectId) {
  return request(axios.delete(`${API_URL}/delete/${sectionId}`, { data: { projectId } }));
}

/** DELETE /deleteAll { projectId, documentKey? } -> { message }. */
export async function deleteAllSections(projectId, documentKey) {
  const body = { projectId };
  if (documentKey) body.documentKey = documentKey;
  return request(axios.delete(`${API_URL}/deleteAll`, { data: body }));
}

/** GET /allVersions/:id?projectId -> { title, versions }. */
export async function getVersions(sectionId, projectId) {
  return request(axios.get(`${API_URL}/allVersions/${sectionId}`, { params: { projectId } }));
}

/** POST /createVersion/:id { projectId, context, prompt, output } -> version. */
export async function createVersion(sectionId, versionData, projectId) {
  return request(axios.post(`${API_URL}/createVersion/${sectionId}`, { ...versionData, projectId }));
}

/** POST /generate/:id - synchronous variant; the UI uses generateIndSectionViaJob instead. */
export async function generateSectionSync(sectionId, projectId, userInstructions) {
  const body = { projectId };
  if (userInstructions) body.userInstructions = userInstructions;
  return request(axios.post(`${API_URL}/generate/${sectionId}`, body, { timeout: 5 * 60 * 1000 }));
}

/** GET /status/:projectId -> { documents: [{ documentKey, total, created, generated, final }] }. */
export async function getStatus(projectId) {
  return request(axios.get(`${API_URL}/status/${projectId}`));
}

// ---------------------------------------------------------------------------
// Background generation (job start + poll; see sourceDocumentJob.service.js)
// ---------------------------------------------------------------------------

/**
 * Generate one IND section through the background-job pipeline and return the
 * generated HTML. Same reasoning as icf/utils/generateIcfContentViaJob.js: a
 * single long HTTP call gets dropped by the Azure front end, a job + poll does not.
 *
 * @param {string} sectionId
 * @param {string} projectId
 * @param {string} [userInstructions] edit instructions for a regenerate
 * @param {(job: object) => void} [onProgress]
 * @returns {Promise<string>} generated section HTML
 */
export async function generateIndSectionViaJob(sectionId, projectId, userInstructions, onProgress) {
  const body = { projectId, sectionId };
  if (userInstructions && String(userInstructions).trim()) {
    body.userInstructions = String(userInstructions).trim();
  }
  const jobResult = await runSourceDocumentJob(SOURCE_DOCUMENT_JOB_TYPES.IND_GENERATE, body, { onProgress });
  return jobResult.output;
}

const indService = {
  getCatalogue,
  getIntake,
  updateIntake,
  autoPopulateIntake,
  extractNonclinical,
  getAllSections,
  initializeDocument,
  getSection,
  updateSection,
  deleteSection,
  deleteAllSections,
  getVersions,
  createVersion,
  generateSectionSync,
  getStatus,
  generateIndSectionViaJob,
};

export default indService;
